/**
 * A tapped button should not stay focused.
 *
 * iOS keeps focus on a button after a tap, and it keeps it until something else
 * takes it. Every `:focus` style the button has then sits there looking like a
 * selection: tap NM, tap LP, and NM is still outlined. It has been fixed one
 * button at a time and one stylesheet rule at a time for months - the border
 * colour on condition pills, the bottom navigation, binders, the label size
 * chips - and it comes back every time somebody adds a control, because nothing
 * about the control is wrong. The platform is doing it.
 *
 * So it is handled once, here. On a touch, the button gives up focus the moment
 * the tap finishes and no focus style has anything to attach to.
 *
 * Only on touch. A keyboard user pressing Tab or Enter keeps focus exactly as
 * they should, because losing it there would leave them with no idea where they
 * are - and `:focus-visible`, which is what keyboard focus draws, is untouched.
 */
export default function dropFocusAfterTap() {
  if (typeof document === 'undefined') return () => {};

  const onTouchEnd = (e) => {
    const el = e.target instanceof Element ? e.target.closest('button, [role="button"]') : null;
    if (!el) return;
    // After the event finishes, so a handler that reads document.activeElement
    // during the tap still sees what it expects.
    setTimeout(() => {
      if (document.activeElement === el) el.blur();
    }, 0);
  };

  document.addEventListener('touchend', onTouchEnd, { passive: true, capture: true });
  return () => document.removeEventListener('touchend', onTouchEnd, { capture: true });
}
